import { storyMs } from './storyTime'
import type { WindKeyframe } from './types'

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Shortest-path angle blend (degrees → degrees). */
export function lerpAngleDeg(a: number, b: number, t: number): number {
  const d = ((((b - a) % 360) + 540) % 360) - 180
  return (((a + d * t) % 360) + 360) % 360
}

export type WindSample = {
  fromDeg: number
  speedMph: number
  plumeDistanceM: number
  plumeSpreadDeg: number
}

/**
 * Sample wind at board elapsed time with smooth blends between sorted keyframes.
 * Before the first frame / after the last: hold that frame.
 * Empty list → null (caller keeps base params).
 */
export function sampleWindAt(frames: WindKeyframe[], playheadIso: string): WindSample | null {
  if (!frames.length) return null
  const elapsedMs = storyMs(playheadIso)
  const sorted = [...frames].sort((a, b) => storyMs(a.at) - storyMs(b.at))
  const first = sorted[0]!
  const last = sorted[sorted.length - 1]!
  if (elapsedMs <= storyMs(first.at)) {
    return {
      fromDeg: first.fromDeg,
      speedMph: first.speedMph,
      plumeDistanceM: first.plumeDistanceM,
      plumeSpreadDeg: first.plumeSpreadDeg,
    }
  }
  if (elapsedMs >= storyMs(last.at)) {
    return {
      fromDeg: last.fromDeg,
      speedMph: last.speedMph,
      plumeDistanceM: last.plumeDistanceM,
      plumeSpreadDeg: last.plumeSpreadDeg,
    }
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i]!
    const b = sorted[i + 1]!
    if (elapsedMs > storyMs(b.at)) continue
    const span = storyMs(b.at) - storyMs(a.at)
    const t = span <= 0 ? 0 : (elapsedMs - storyMs(a.at)) / span
    return {
      fromDeg: lerpAngleDeg(a.fromDeg, b.fromDeg, t),
      speedMph: lerp(a.speedMph, b.speedMph, t),
      plumeDistanceM: lerp(a.plumeDistanceM, b.plumeDistanceM, t),
      plumeSpreadDeg: lerp(a.plumeSpreadDeg, b.plumeSpreadDeg, t),
    }
  }
  return {
    fromDeg: last.fromDeg,
    speedMph: last.speedMph,
    plumeDistanceM: last.plumeDistanceM,
    plumeSpreadDeg: last.plumeSpreadDeg,
  }
}
